import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import type { SxProps, Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { CONTAINER_SCROLL_LOCK_CLASS } from '@openx/constants/styles';
import type { ReactNode, RefObject } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatBubbleIcon } from './ChatBubbleIcon';
import { FLOATING_WIDGET_OFFSET, FLOATING_WIDGET_SIZE } from './constants';
import type { FloatingPosition } from './types';
import { useFloatingPosition } from './useFloatingPosition';
import { getFixedPositionStyles, getOverlayBounds } from './utils';

export interface FloatingWidgetProps {
  storageKey: string;
  content: ReactNode;
  label?: string;
  defaultPosition?: FloatingPosition;
  size?: number;
  containerRef?: RefObject<HTMLElement | null>;
  'data-test'?: string;
  keepMounted?: boolean;
}

export function FloatingWidget({
  storageKey,
  content,
  label = 'AI',
  defaultPosition = 'right',
  size = FLOATING_WIDGET_SIZE,
  containerRef,
  'data-test': dataTest = 'floating-widget',
  keepMounted = false,
}: FloatingWidgetProps) {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [widgetSlot, setWidgetSlot] = useFloatingPosition(storageKey, defaultPosition);
  const [isDragging, setIsDragging] = useState(false);

  const createDropHandlers = (slot: FloatingPosition) => ({
    onDragOver: (e: React.DragEvent) => e.preventDefault(),
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      setWidgetSlot(slot);
      setIsDragging(false);
    },
  });

  const openModal = () => {
    containerRef?.current?.classList.add(CONTAINER_SCROLL_LOCK_CLASS);
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    containerRef?.current?.classList.remove(CONTAINER_SCROLL_LOCK_CLASS);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <false positive>
  useEffect(() => {
    return () => {
      containerRef?.current?.classList.remove(CONTAINER_SCROLL_LOCK_CLASS);
    };
  }, []);

  const containerRect = containerRef?.current?.getBoundingClientRect() ?? null;
  const slotSx: SxProps<Theme> = {
    position: 'fixed',
    width: size * 2,
    height: size * 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: isDragging ? 'auto' : 'none',
  };

  const highlightSx: SxProps<Theme> = {
    position: 'absolute',
    inset: 0,
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
    opacity: isDragging ? 1 : 0,
    transition: 'opacity 0.2s ease',
    pointerEvents: 'none',
  };

  const triggerButton = (
    <IconButton
      draggable
      onDragStart={() => setIsDragging(true)}
      onDragEnd={() => setIsDragging(false)}
      onClick={openModal}
      sx={{
        pointerEvents: 'auto',
        width: size,
        height: size,
        backgroundColor: theme => theme.palette.primary.main,
        '&:hover': {
          backgroundColor: theme => theme.palette.primary.dark,
        },
      }}
      aria-label={isOpen ? t('Close') : t('Open')}
      data-test={`${dataTest}-trigger`}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: theme => theme.palette.primary.light,
        }}
      >
        <ChatBubbleIcon width={27} height={27} />
        <Typography
          variant="body1Bold"
          sx={{
            position: 'absolute',
            color: theme => theme.palette.primary.dark,
            top: '40%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          {label}
        </Typography>
      </Box>
    </IconButton>
  );

  return (
    <>
      <Box
        component="div"
        data-test={`${dataTest}-slot-left`}
        data-slot="left"
        sx={{ ...slotSx, ...getFixedPositionStyles('left', containerRect, 0) }}
        {...createDropHandlers('left')}
      >
        <Box sx={highlightSx} aria-hidden />
        {widgetSlot === 'left' && triggerButton}
      </Box>
      <Box
        component="div"
        data-test={`${dataTest}-slot-right`}
        data-slot="right"
        sx={{ ...slotSx, ...getFixedPositionStyles('right', containerRect, 0) }}
        {...createDropHandlers('right')}
      >
        <Box sx={highlightSx} aria-hidden />
        {widgetSlot === 'right' && triggerButton}
      </Box>
      {(isOpen || keepMounted) && (
        <Box
          onClick={closeModal}
          sx={{
            display: isOpen ? 'block' : 'none',
            position: 'fixed',
            ...getOverlayBounds(containerRef?.current?.getBoundingClientRect() ?? null),
            zIndex: theme => theme.zIndex.modal - 1,
            bgcolor: theme => alpha(theme.palette.common.black, 0.5),
            '@keyframes fadeIn': {
              from: { opacity: 0 },
              to: { opacity: 1 },
            },
            animation: 'fadeIn 0.2s ease-out',
          }}
          data-test={`${dataTest}-overlay`}
        >
          <Box
            onClick={e => e.stopPropagation()}
            sx={{
              position: 'absolute',
              top: FLOATING_WIDGET_OFFSET,
              left: widgetSlot === 'left' ? FLOATING_WIDGET_OFFSET + size + 12 : FLOATING_WIDGET_OFFSET,
              bottom: FLOATING_WIDGET_OFFSET,
              right: widgetSlot === 'right' ? FLOATING_WIDGET_OFFSET + size + 12 : FLOATING_WIDGET_OFFSET,
              overflow: 'auto',
              backgroundColor: theme => theme.palette.background.paper,
              '@keyframes slideIn': {
                from: { opacity: 0, transform: 'translateY(12px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
              animation: 'slideIn 0.25s ease-out',
            }}
            data-test={`${dataTest}-content`}
          >
            <IconButton
              onClick={closeModal}
              aria-label={t('Close')}
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                zIndex: 1,
              }}
              data-test={`${dataTest}-close`}
            >
              <KeyboardArrowDown />
            </IconButton>
            {content}
          </Box>
        </Box>
      )}
    </>
  );
}
